'use client'

import ViverseButton from '@/components/ViverseButton'
import { usePartnerApply } from '@/hooks/usePartnerApply'
import { motion } from 'framer-motion'
import { useTranslations } from 'next-intl'

export default function HowToApplySection() {
  const t = useTranslations()
  const { handleClickApplyButton } = usePartnerApply()

  const steps = [
    {
      number: '1',
      title: t('partner.how-to-apply.step1.title'),
      description: t('partner.how-to-apply.step1.description'),
    },
    {
      number: '2',
      title: t('partner.how-to-apply.step2.title'),
      description: t('partner.how-to-apply.step2.description'),
    },
    {
      number: '3',
      title: t('partner.how-to-apply.step3.title'),
      description: t('partner.how-to-apply.step3.description'),
    },
  ]

  return (
    <section className="bg-background relative flex flex-col items-center px-4 py-12 md:px-8 md:py-20 lg:px-18 lg:py-2">
      <div className="flex w-full max-w-[1296px] flex-col items-center gap-6">
        <motion.div
          className="flex flex-col items-center gap-6"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <h2 className="text-center text-[28px] leading-[30px] font-extrabold tracking-[0.15px] text-white md:text-[38px] md:leading-10">
            {t('partner.how-to-apply.title')}
          </h2>
          <p className="text-vive-primary-20 text-center text-base leading-[26px] tracking-[0.25px] md:text-[18px] md:leading-8 lg:max-w-[800px]">
            {t('partner.how-to-apply.subtitle')}
          </p>
        </motion.div>

        <ol className="flex list-none flex-col gap-6 md:grid md:grid-cols-2 lg:grid lg:grid-cols-3">
          {steps.map((step, index) => (
            <motion.li
              key={step.title}
              className="flex w-full flex-col gap-2 rounded-3xl bg-linear-to-b from-[rgba(42,54,73,0.5)] to-[rgba(42,54,73,0.3)] p-6 md:max-w-[326px]"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.6, ease: 'easeOut', delay: index * 0.12 }}
            >
              <div
                className="flex h-14 w-14 items-center justify-center rounded-3xl bg-linear-to-b from-[rgba(42,54,73,0.5)] to-[rgba(42,54,73,0.3)]"
                aria-label={`Step ${step.number}`}
              >
                <span className="text-[16px] leading-6 font-semibold tracking-[0.2px] text-white">
                  {step.number}
                </span>
              </div>

              <h3 className="text-[16px] leading-6 font-semibold tracking-[0.2px] text-white">
                {step.title}
              </h3>

              <p className="text-vive-primary-20 text-[16px] leading-6 font-normal tracking-[0.2px]">
                {step.description}
              </p>
            </motion.li>
          ))}
        </ol>
        <motion.div
          className="flex flex-col items-center gap-6"
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.8, ease: 'easeOut' }}
        >
          <p className="text-vive-primary-30 text-center text-[14px] leading-[22px] font-normal tracking-[0.2px] md:max-w-[630px]">
            {t.rich('partner.how-to-apply.footer', {
              wording: t('partner.how-to-apply.footer-emphasis'),
              strong: (chunks) => <strong className="font-bold">{chunks}</strong>,
            })}
          </p>

          <ViverseButton
            size="medium"
            variant="colorful"
            className="min-h-12 text-[18px]"
            onClick={handleClickApplyButton}
          >
            {t('partner.how-to-apply.apply-button')}
          </ViverseButton>
        </motion.div>
      </div>
    </section>
  )
}
